import React from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  TextField,
  Paper,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  InputAdornment,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PrintIcon from '@mui/icons-material/Print';
import firebase from '../../utilities/firebase';
import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';
import { printQualityWorksheet } from './QualityWorksheetPrint';
import {
  checkDone,
  checkFailed,
  countsToward,
  isCore,
  checkServiceIds,
  serviceRollup,
  computeQualitySummary,
} from './qualityChecks';
import moment from 'moment';

// Re-exported so existing importers (the SO modal's completion gate) keep
// working against this module.
export { computeQualitySummary };

// Disclosure key for the shared vehicle block, which has no serviceId.
const VEHICLE_KEY = '__vehicle';

// Derive in/out-of-spec for a measured check given a recorded actual reading.
// Returns null when there's nothing to judge yet.
const measuredResult = (check, actual) => {
  if (actual === '' || actual === null || actual === undefined) return null;
  const a = Number(actual);
  if (Number.isNaN(a)) return null;
  const lo = typeof check.min === 'number' ? check.min : -Infinity;
  const hi = typeof check.max === 'number' ? check.max : Infinity;
  return a >= lo && a <= hi ? 'in_spec' : 'out_of_spec';
};

const methodLabels = {
  measured: 'Measured',
  road_test: 'Road test',
  obd_scan: 'OBD scan',
  visual: 'Visual',
  functional: 'Functional',
};

const specText = (c) => {
  const parts = [];
  if (typeof c.expected === 'number') parts.push(`target ${c.expected}`);
  if (typeof c.min === 'number' || typeof c.max === 'number') {
    parts.push(`${typeof c.min === 'number' ? c.min : '–'}…${typeof c.max === 'number' ? c.max : '–'}`);
  }
  const body = parts.join('  ');
  return c.unit ? `${body} ${c.unit}`.trim() : body;
};

export default function QualityTab({ order, stockNumber, setOrder }) {
  const [generating, setGenerating] = React.useState(false);
  // Sections whose extended checks are showing, keyed by serviceId (plus
  // VEHICLE_KEY for the shared block).
  const [expanded, setExpanded] = React.useState(() => new Set());

  const checks = React.useMemo(() => order.qualityChecks || [], [order.qualityChecks]);
  // Declined lines are excluded here so the sections, the header chip, and the
  // generator all work from the same service list. Checks belonging to a service
  // declined after generation fall through to the trailing group below.
  const services = React.useMemo(() => (order.services || []).filter(s => !s.declined), [order.services]);
  const loaded = order.qualityChecks_loaded;
  const gen = order.qualityGeneration || null;
  const user = StateManager.userName || 'staff';

  // Optimistic local update + background write. No blocking spinner — the failure
  // path is the only thing that surfaces to the user.
  const updateCheck = (id, updates) => {
    setOrder(prev => ({
      ...prev,
      qualityChecks: (prev.qualityChecks || []).map(c => (c.id === id ? { ...c, ...updates } : c)),
    }));
    firebase.firestore().collection('qualityChecks').doc(id).update(updates)
      .catch(() => StateManager.setAlertAndOpen('Failed to save quality check', 'error'));
  };

  const stamp = () => ({ completed_by: user, completed_at: moment().format('YYYY/MM/DD HH:mm') });

  const recordActual = (check, value) => {
    const cleaned = value === '' ? null : value;
    const merged = { ...check, actual: cleaned };
    updateCheck(check.id, {
      actual: cleaned === null ? null : Number(cleaned),
      result: measuredResult(merged, cleaned),
      ...(cleaned === null ? {} : stamp()),
    });
  };

  const editSpec = (check, field, value) => {
    const num = value === '' ? null : Number(value);
    const merged = { ...check, [field]: num };
    const updates = { [field]: Number.isNaN(num) ? null : num, source: 'edited' };
    // Re-derive the pass/fail if a reading is already recorded against the new spec.
    if (check.actual !== null && check.actual !== undefined) {
      updates.result = measuredResult(merged, check.actual);
    }
    updateCheck(check.id, updates);
  };

  const editUnit = (check, value) => updateCheck(check.id, { unit: value, source: 'edited' });

  const setMultistate = (check, result) => {
    updateCheck(check.id, { result, ...(result ? stamp() : {}) });
  };

  const setNote = (check, note) => updateCheck(check.id, { note });

  const generate = async (mode) => {
    if (generating) return;
    setGenerating(true);
    StateManager.setAlertAndOpen(
      mode === 'full' ? 'Regenerating quality checklist…' : 'Generating quality checklist…',
      'info',
    );
    try {
      const res = await RequestManager.post({
        function: 'generateQualityChecklist',
        variables: { serviceOrderId: stockNumber, mode },
      });
      if (!res || res.success === false) {
        StateManager.setAlertAndOpen('Failed to generate quality checklist', 'error');
      } else if ((res.checksGenerated || 0) === 0 && (res.staleFlagged || 0) === 0) {
        StateManager.setAlertAndOpen('Checklist already up to date', 'info');
      } else {
        StateManager.setAlertAndOpen(`Generated ${res.checksGenerated || 0} check(s)`, 'success');
      }
    } catch {
      StateManager.setAlertAndOpen('Failed to generate quality checklist', 'error');
    } finally {
      setGenerating(false);
    }
  };

  // No services/{id}.qualityStatus mirror is written here. Nothing in either
  // repo ever read it, and it cost a write per service on every snapshot — from
  // a phone in a shop bay. Status is derived from the checks wherever it's shown.

  const uploadPhoto = async (serviceId, file) => {
    if (!file) return;
    StateManager.setAlertAndOpen('Uploading photo…', 'info');
    try {
      const safe = String(file.name || 'photo').replace(/[^\w.-]/g, '_');
      const path = `qualityPhotos/${stockNumber}/${serviceId}/${Date.now()}_${safe}`;
      const ref = firebase.storage().ref(path);
      await ref.put(file);
      const url = await ref.getDownloadURL();
      await firebase.firestore().collection('services').doc(serviceId).update({
        qualityPhotos: firebase.firestore.FieldValue.arrayUnion({
          url,
          name: file.name || 'photo',
          uploadedBy: user,
          uploadedAt: moment().format('YYYY/MM/DD HH:mm'),
        }),
      });
      StateManager.setAlertAndOpen('Photo added', 'success');
    } catch {
      StateManager.setAlertAndOpen('Failed to upload photo', 'error');
    }
  };

  const printWorksheet = async () => {
    const ok = await printQualityWorksheet({ order, stockNumber });
    if (!ok) StateManager.setAlertAndOpen('Could not open the print window (popup blocked?)', 'error');
  };

  const summary = computeQualitySummary(order);

  // Vehicle-level checks are shown once, up top — they're done once for the whole
  // car and satisfy every service line they cover.
  const vehicleChecks = React.useMemo(() => checks.filter(c => c.scope === 'vehicle'), [checks]);

  // What's holding a section back, and whether all of it lives in the shared
  // vehicle block. A failed vehicle check covers every service it was generated
  // against, so without this the section reads "Failed" above a column of
  // Passes and there's nothing on screen to explain it.
  const blockersFor = (rollupChecks) => {
    const blockers = (rollupChecks || []).filter(c => countsToward(c) && (checkFailed(c) || c.stale));
    return {
      blockers,
      vehicleOnly: blockers.length > 0 && blockers.every(c => c.scope === 'vehicle'),
    };
  };

  // Per-service groups, ordered to match the SO's service list. Each group shows
  // only its own service-scope checks (`checks`), but its status rolls up over
  // everything covering it (`rollupChecks`, incl. shared vehicle checks). Any
  // service-scope checks whose service is gone fall into a trailing group.
  const groups = React.useMemo(() => {
    const coverByService = {}; // all checks covering a service — drives status
    const ownByService = {};   // service-scope checks shown under the service
    for (const c of checks) {
      for (const sid of checkServiceIds(c)) {
        (coverByService[sid] = coverByService[sid] || []).push(c);
        if (c.scope !== 'vehicle') (ownByService[sid] = ownByService[sid] || []).push(c);
      }
    }
    const ordered = [];
    const seen = new Set();
    for (const s of services) {
      if (coverByService[s.id]) {
        ordered.push({
          serviceId: s.id,
          name: s.name || s.description || 'Service',
          tag: s.tag,
          checks: ownByService[s.id] || [],
          rollupChecks: coverByService[s.id],
          photos: s.qualityPhotos || [],
        });
        seen.add(s.id);
      }
    }
    for (const sid of Object.keys(ownByService)) {
      if (!seen.has(sid)) {
        ordered.push({ serviceId: sid, name: ownByService[sid][0].serviceName || 'Service', checks: ownByService[sid], rollupChecks: ownByService[sid], photos: [] });
      }
    }
    return ordered;
  }, [checks, services]);

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const headerBar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScienceIcon color="primary" />
        <Typography variant="h6">Quality Checks</Typography>
      </Box>
      {checks.length > 0 && summary.total > 0 && (
        <Chip
          size="small"
          variant="outlined"
          color={summary.allClear ? 'success' : (summary.failed || summary.needsRecheck ? 'warning' : 'default')}
          label={`${summary.passed}/${summary.total} services passed`}
        />
      )}
      <Box sx={{ flex: 1 }} />
      {gen && gen.status === 'running' && (
        <Chip size="small" color="info" variant="outlined" icon={<CircularProgress size={12} />} label="Generating…" />
      )}
      {gen && gen.status === 'failed' && (
        <Tooltip title={gen.error || 'Generation failed'}>
          <Chip size="small" color="error" variant="outlined" label="Auto-generation failed" />
        </Tooltip>
      )}
      {checks.length > 0 && (
        <>
          <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={printWorksheet}>
            Print worksheet
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={generating ? <CircularProgress size={14} /> : <RefreshIcon />}
            disabled={generating}
            onClick={() => generate('reconcile')}
          >
            Regenerate
          </Button>
        </>
      )}
    </Box>
  );

  if (checks.length === 0) {
    return (
      <Box>
        {headerBar}
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No quality checklist yet. It’s generated automatically when this service order enters the{' '}
            <b>quality</b> stage — or you can generate it now from the services on this order.
          </Typography>
          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={generating || services.length === 0}
            onClick={() => generate('reconcile')}
          >
            Generate checklist
          </Button>
          {services.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Add services to this order first.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  const resultChip = (result) => {
    if (result === 'in_spec') return <Chip size="small" label="In spec" sx={{ bgcolor: 'success.dark', color: '#fff' }} />;
    if (result === 'out_of_spec') return <Chip size="small" color="error" label="Out of spec" />;
    if (result === 'pass') return <Chip size="small" label="Pass" sx={{ bgcolor: 'success.dark', color: '#fff' }} />;
    if (result === 'fail') return <Chip size="small" color="error" label="Fail" />;
    if (result === 'na') return <Chip size="small" variant="outlined" label="N/A" />;
    return null;
  };

  const renderCheck = (c, num) => {
    const derived = c.type === 'measured' ? measuredResult(c, c.actual) : c.result;
    return (
      <Box key={c.id} sx={{ py: 1.5 }}>
        {/* Label on the left, the check-off input aligned on the right so the
            whole list can be worked top-to-bottom down a single input column.
            The leading number matches the "#" column on the printed worksheet
            (per-group, restarting at 1) so the two can be traced against each other. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {num != null && (
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', minWidth: 18, textAlign: 'right' }}>
                {num}.
              </Typography>
            )}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.label}</Typography>
            {c.method && <Chip size="small" variant="outlined" label={methodLabels[c.method] || c.method} />}
            {!isCore(c) && (
              <Tooltip title="Extra depth — not required to pass the order.">
                <Chip size="small" variant="outlined" label="Extended" />
              </Tooltip>
            )}
            {c.stale && (
              <Tooltip title="The service changed after this check was generated — re-verify.">
                <Chip size="small" color="warning" variant="outlined" label="Needs re-check" />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexShrink: 0, ml: 'auto' }}>
            {c.type === 'measured' && (
              <TextField
                label="Actual" size="small" type="number"
                defaultValue={c.actual ?? ''} sx={{ width: 130 }}
                InputProps={c.unit ? { endAdornment: <InputAdornment position="end">{c.unit}</InputAdornment> } : undefined}
                onBlur={(e) => recordActual(c, e.target.value)}
              />
            )}
            {c.type === 'multistate' && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={c.result || null}
                onChange={(e, v) => v && setMultistate(c, v)}
              >
                <ToggleButton value="pass" color="success">Pass</ToggleButton>
                <ToggleButton value="fail" color="error">Fail</ToggleButton>
                <ToggleButton value="na">N/A</ToggleButton>
              </ToggleButtonGroup>
            )}
            {c.type !== 'multistate' && resultChip(derived)}
          </Box>
        </Box>

        {c.type === 'measured' && (
          <Tooltip title="Suggested spec — edit to match the service manual">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
              <TextField
                label="Min" size="small" type="number" variant="standard"
                defaultValue={c.min ?? ''} sx={{ width: 70 }}
                onBlur={(e) => editSpec(c, 'min', e.target.value)}
              />
              <TextField
                label="Target" size="small" type="number" variant="standard"
                defaultValue={c.expected ?? ''} sx={{ width: 70 }}
                onBlur={(e) => editSpec(c, 'expected', e.target.value)}
              />
              <TextField
                label="Max" size="small" type="number" variant="standard"
                defaultValue={c.max ?? ''} sx={{ width: 70 }}
                onBlur={(e) => editSpec(c, 'max', e.target.value)}
              />
              <TextField
                label="Unit" size="small" variant="standard"
                defaultValue={c.unit || ''} sx={{ width: 60 }}
                onBlur={(e) => editUnit(c, e.target.value)}
              />
            </Box>
          </Tooltip>
        )}

        <Box sx={{ mt: 1 }}>
          <TextField
            placeholder={c.type === 'text' ? 'Observation…' : 'Note (optional)'}
            size="small" fullWidth variant="standard"
            defaultValue={c.note || ''}
            onBlur={(e) => setNote(c, e.target.value)}
          />
        </Box>

        {(c.completed_by || specText(c)) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {c.type === 'measured' && specText(c) ? `Spec: ${specText(c)}` : ''}
            {c.completed_by ? `${c.type === 'measured' && specText(c) ? ' · ' : ''}${c.completed_by}${c.completed_at ? ` · ${c.completed_at}` : ''}` : ''}
          </Typography>
        )}
      </Box>
    );
  };

  // `attribution` names what's blocking when the cause sits in the shared
  // vehicle block rather than in the rows printed under this section.
  const statusChipFor = (status, attribution) => {
    const { vehicleOnly, blockers } = attribution || {};
    if (vehicleOnly && (status === 'failed' || status === 'needs_recheck')) {
      const label = status === 'failed' ? 'Failed — vehicle check' : 'Needs re-check — vehicle check';
      const names = blockers.map(c => c.label).join(', ');
      return (
        <Tooltip title={`From the vehicle checks above: ${names}`}>
          <Chip
            size="small"
            label={label}
            color={status === 'failed' ? 'error' : 'warning'}
            variant={status === 'failed' ? 'filled' : 'outlined'}
          />
        </Tooltip>
      );
    }
    return {
      passed: <Chip size="small" label="Passed" sx={{ bgcolor: 'success.dark', color: '#fff' }} />,
      failed: <Chip size="small" color="error" label="Failed" />,
      needs_recheck: <Chip size="small" color="warning" variant="outlined" label="Needs re-check" />,
      in_progress: <Chip size="small" variant="outlined" label="In progress" />,
      none: null,
    }[status];
  };

  // A section's checks split into the short required list and the deeper set
  // behind the disclosure. Extended rows someone has already worked on are
  // shown regardless, so nothing in flight disappears under a mechanic.
  const partitionChecks = (list, key) => {
    const core = list.filter(isCore);
    const extended = list.filter(c => !isCore(c));
    const touched = extended.some(c => checkDone(c) || checkFailed(c));
    return { core, extended, open: touched || expanded.has(key) };
  };

  const toggleExpanded = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Numbering continues across the disclosure so a row read off the printed
  // worksheet's "#" column still matches what's on screen.
  const renderSectionChecks = (list, key) => {
    const { core, extended, open } = partitionChecks(list, key);
    return (
      <>
        {core.map((c, i) => (
          <React.Fragment key={c.id}>
            {i > 0 && <Divider light />}
            {renderCheck(c, i + 1)}
          </React.Fragment>
        ))}
        {extended.length > 0 && (
          <>
            <Collapse in={open} unmountOnExit>
              {extended.map((c, i) => (
                <React.Fragment key={c.id}>
                  {(i > 0 || core.length > 0) && <Divider light />}
                  {renderCheck(c, core.length + i + 1)}
                </React.Fragment>
              ))}
            </Collapse>
            <Box sx={{ pt: 1 }}>
              <Button
                size="small"
                variant="text"
                startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => toggleExpanded(key)}
              >
                {open ? `Hide ${extended.length} extra check${extended.length === 1 ? '' : 's'}`
                      : `Show ${extended.length} more check${extended.length === 1 ? '' : 's'}`}
              </Button>
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <Box>
      {headerBar}
      {vehicleChecks.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.light', bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Vehicle checks</Typography>
            <Chip size="small" variant="outlined" color="primary" label="Perform once" />
            {statusChipFor(serviceRollup(vehicleChecks))}
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Done once for the whole car — covers every service below.
            </Typography>
          </Box>
          <Divider />
          {renderSectionChecks(vehicleChecks, VEHICLE_KEY)}
        </Paper>
      )}
      {groups.map((g) => {
        const statusChip = statusChipFor(serviceRollup(g.rollupChecks), blockersFor(g.rollupChecks));
        return (
        <Paper key={g.serviceId} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{g.name}</Typography>
            {g.tag && <Chip size="small" variant="outlined" label={g.tag} />}
            {statusChip}
            <Box sx={{ flex: 1 }} />
            {(g.photos || []).map((p, pi) => (
              <Tooltip key={pi} title={`${p.name || 'Photo'}${p.uploadedBy ? ` · ${p.uploadedBy}` : ''}`}>
                <Box
                  component="img"
                  src={p.url}
                  alt="quality"
                  onClick={() => window.open(p.url, '_blank')}
                  sx={{ width: 34, height: 34, borderRadius: 1, objectFit: 'cover', cursor: 'pointer', border: '1px solid', borderColor: 'divider' }}
                />
              </Tooltip>
            ))}
            <Button size="small" component="label" startIcon={<PhotoCameraIcon />} sx={{ minWidth: 0 }}>
              Photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; uploadPhoto(g.serviceId, f); }}
              />
            </Button>
          </Box>
          <Divider />
          {g.checks.length > 0 ? (
            renderSectionChecks(g.checks, g.serviceId)
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
              Verified by the vehicle checks above.
            </Typography>
          )}
        </Paper>
        );
      })}
    </Box>
  );
}
